<template>
  <WCCheckoutStage
    @checkout="$emit('checkout', $event)"
    :activeStage="activeStage"
    :stage="0"
    label="1. Review Your Order"
  >
    <PlatformRenderer>
      <template v-slot:onMobile>
        <div>
          <div class="d-flex flex-column mb-3" v-if="!giftCardItems.length">
            <WCFormGroup>
              <label for="order-sidebar__text-area"
                ><b>{{ $t('orderNotes') }}</b></label
              >
              <textarea
                id="order-sidebar__text-area"
                :value="instructions"
                @input="
                  $emit('instructions', $event.target.value);
                  $emit('set-dirty', true);
                "
                class="form-control bg-light border-dark"
                placeholder="Add notes about this order (optional)"
              />
            </WCFormGroup>

            <WCCheckbox
              class="mb-4"
              label="Allow substitutions on all items"
              :value="true"
              :model="allSubstitutable"
              @change="setAllSubstitutable"
            />
            <WCMobileCoupons
              :coupons="coupons"
              :lineItems="lineItems"
              :addedCouponValid="addedCouponValid"
              @addcoupon="$emit('addcoupon', $event)"
              @removecoupon="$emit('removecoupon', $event)"
            />
            <WCSpecialPromos
              :deferrableCombos="deferrableCombos"
              @defer="
                setDeferrableCombos($event);
                $emit('set-dirty', true);
              "
              class="mt-2"
            />
            <h3 class="mb-0">
              {{ $t('reviewYourItems') }}
            </h3>

            <WCOutOfStockAlert
              v-if="outofstockLineItems && outofstockLineItems.length > 0"
              :outofstockLineItems="outofstockLineItems"
            />

            <WCCheckoutLineItems
              :lineItems="lineItems"
              @instructions="
                setItemLineItemInstructions($event);
                $emit('set-dirty', true);
              "
              @substitutable="
                setItemLineItemSubstitutable($event);
                $emit('set-dirty', true);
              "
            />

            <WCSpecialOrderButton
              v-if="$configuration.allowSpecialOrder && !isScanAndGoMode"
              class="btn btn-primary btn-block mb-3"
              @click="openSpecialOrderModal($refs.specialOrderBtnRef)"
              ref="specialOrderBtnRef"
            />
          </div>
          <template v-else>
            <WCCheckoutGiftCardLineItems :giftCardItems="giftCardItems" />
          </template>
        </div>
      </template>

      <template v-slot:onDesktop>
        <div>
          <template v-if="!giftCardItems.length">
            <div class="d-flex justify-content-between mb-3">
              <div class="w-50">
                <WCFormGroup v-if="!giftCardItems.length">
                  <label for="order-sidebar__text-area" class="h5 font-weight-bold">{{
                    $t('orderNotes')
                  }}</label>
                  <textarea
                    id="order-sidebar__text-area"
                    :value="instructions"
                    @input="
                      $emit('instructions', $event.target.value);
                      $emit('set-dirty', true);
                    "
                    class="form-control font-size-lg border-dark"
                    placeholder="Add notes about this order (optional)"
                  />
                </WCFormGroup>

                <WCCheckbox
                  class="mb-4 font-size-md font-weight-bold"
                  label="Allow substitutions on all items"
                  :value="true"
                  :model="allSubstitutable"
                  @change="setAllSubstitutable"
                />
              </div>
              <div class="wc-promos-section">
                <WCCoupons
                  :coupons="coupons"
                  :lineItems="lineItems"
                  :addedCouponValid="addedCouponValid"
                  @addcoupon="$emit('addcoupon', $event)"
                  @removecoupon="$emit('removecoupon', $event)"
                />
                <WCSpecialPromos
                  :deferrableCombos="deferrableCombos"
                  @defer="
                    setDeferrableCombos($event);
                    $emit('set-dirty', true);
                  "
                  class="mt-2"
                />
              </div>
            </div>

            <h3 class="mb-0">
              {{ $t('reviewYourItems') }}
            </h3>
            <hr class="border-secondary" />
            <WCOutOfStockAlert
              v-if="outofstockLineItems && outofstockLineItems.length > 0"
              :outofstockLineItems="outofstockLineItems"
            />
            <WCCheckoutLineItems
              :lineItems="lineItems"
              @instructions="
                setItemLineItemInstructions($event);
                $emit('set-dirty', true);
              "
              @substitutable="
                setItemLineItemSubstitutable($event);
                $emit('set-dirty', true);
              "
            />
          </template>
          <template v-else>
            <WCCheckoutGiftCardLineItems :giftCardItems="giftCardItems" />
          </template>
        </div>
      </template>
    </PlatformRenderer>

    <button
      @click="$emit('checkout')"
      class="btn btn-primary btn-block w-25 ml-auto stage-button mt-3"
    >
      {{ $t('continue') }}
    </button>
  </WCCheckoutStage>
</template>

<script>
import { mapActions } from 'vuex';
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import WCCheckbox from '@/modules/forms/components/WCCheckbox/WCCheckbox.vue';
import WCFormGroup from '@/modules/forms/components/WCFormGroup/WCFormGroup.vue';
import ModalService from '@/modules/modals/services/modal.service';
import WCSpecialOrderButton from '@/modules/cart/components/WCSpecialOrderButton/WCSpecialOrderButton.vue';
import WCSpecialOrderModal from '@/modules/cart/components/WCSpecialOrderModal/WCSpecialOrderModal.vue';
import WCOutOfStockAlert from '@/modules/cart/components/WCOutOfStockAlert/WCOutOfStockAlert.vue';
import WCCheckoutLineItems from './WCCheckoutLineItems/WCCheckoutLineItems.vue';
import WCCheckoutStage from '../WCCheckoutStage/WCCheckoutStage.vue';
import WCCoupons from '../WCCoupons/WCCoupons.vue';
import WCMobileCoupons from '../WCMobileCoupons/WCMobileCoupons.vue';
import WCSpecialPromos from '../WCSpecialPromos/WCSpecialPromos.vue';
import WCCheckoutGiftCardLineItems from './WCCheckoutGiftCardLineItems/WCCheckoutGiftCardLineItems.vue';

export default {
  components: {
    WCCheckoutStage,
    WCCheckbox,
    WCCheckoutLineItems,
    WCFormGroup,
    WCCoupons,
    PlatformRenderer,
    WCMobileCoupons,
    WCSpecialPromos,
    WCCheckoutGiftCardLineItems,
    WCOutOfStockAlert,
    WCSpecialOrderButton,
  },
  props: {
    activeStage: {
      type: Number,
      required: true,
    },
    instructions: {
      type: String,
    },
    lineItems: {
      type: Array,
    },
    outofstockLineItems: {
      type: Array,
    },
    coupons: {
      type: Array,
    },
    addedCouponValid: {
      type: Boolean,
      default: true,
    },
    deferrableCombos: {
      type: Array,
    },
    giftCardItems: {
      type: Array,
    },
    isScanAndGoMode: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    ...mapActions({
      setItemLineItemInstructions: 'cart/setItemLineItemInstructions',
      setItemLineItemSubstitutable: 'cart/setItemLineItemSubstitutable',
      setDeferrableCombos: 'cart/setDeferrableCombos',
    }),
    getSalesUnit(item) {
      if (item.item.weightProfile && item.item.weightProfile.abbrv) {
        return item.item.weightProfile.abbrv;
      }

      return 'ea';
    },
    isDividedPrice(item) {
      return item.item.actualPriceDivider && item.item.actualPriceDivider > 1;
    },
    setAllSubstitutable() {
      this.$emit('set-dirty', true);
      const newSubstitutionStatus = !this.allSubstitutable;
      this.itemLineItems.forEach(lineItem => {
        this.setItemLineItemSubstitutable({
          item: lineItem.item,
          variation: lineItem.variation,
          substitutable: newSubstitutionStatus,
        });
      });
    },
    async openSpecialOrderModal(specialOrderBtnRef) {
      try {
        await ModalService.open(WCSpecialOrderModal, {
          lineItems: this.lineItems,
        });
      } catch (error) {
        // NOOP
      } finally {
        if (specialOrderBtnRef) specialOrderBtnRef.focus();
      }
    },
  },
  computed: {
    itemLineItems() {
      // Promos can be line items too, but we don't want to list them with items
      return this.lineItems.filter(lineItem => lineItem.type === 1);
    },
    allSubstitutable() {
      let allSubstitutable = true;
      this.itemLineItems.forEach(lineItem => {
        if (!lineItem.substitutable) {
          allSubstitutable = false;
        }
      });
      return allSubstitutable;
    },
  },
};
</script>

<style lang="scss" scoped>
.wc-promos-section {
  width: 40%;
}
.stage-button {
  min-width: 33%;
}
</style>
